import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { action, set, toJS } from 'mobx';
import noop from 'lodash/noop';
import flatten from 'lodash/flatten';
import Collapse from 'choerodon-ui/lib/collapse';
import CollapsePanel from 'choerodon-ui/lib/collapse/CollapsePanel';
import { getColumnKey, getColumnLock } from '../utils';
import ColumnGroups from './column-groups';
import DataSet from '../../data-set/DataSet';
import { treeReduce } from '../../_util/treeUtils';
import TableContext from '../TableContext';
import { $l } from '../../locale-context';
import Button from '../../button/Button';
import { ButtonColor, FuncType } from '../../button/enum';
import { Size } from '../../core/enum';
import { mergeDefaultProps, normalizeColumns, SELECTION_KEY } from '../TableStore';
import Form from '../../form/Form';
import ObserverNumberField from '../../number-field/NumberField';
import SelectBox from '../../select-box/SelectBox';
import Option from '../../option/Option';
import { TableHeightType } from '../enum';
import { LabelLayout } from '../../form/enum';
import { ShowHelp } from '../../field/enum';
import { ViewMode } from '../../radio/enum';
import Icon from '../../icon';
import Tooltip from '../../tooltip/Tooltip';
function normalizeColumnsToTreeData(columns) {
    return treeReduce(columns, (list, column, _sort, parentColumn) => list.concat({
        key: getColumnKey(column),
        parentKey: parentColumn && getColumnKey(parentColumn),
        lock: getColumnLock(column.lock),
        width: column.width,
        header: column.header,
        title: column.title,
        hidden: column.hidden,
        name: column.name,
        sort: column.sort,
        titleEditable: column.titleEditable,
        hideable: column.hideable,
    }), []);
}
function diff(height = 0) {
    if (typeof document !== 'undefined') {
        return document.documentElement.clientHeight - height;
    }
    return 0;
}
const HEIGHT_CHANGE_KEY = '__heightChange__';
const CustomizationSettings = observer(function CustomizationSettings(props) {
    const { modal } = props;
    const { handleOk, handleCancel } = modal || { update: noop, handleOk: noop };
    const { prefixCls, tableStore } = useContext(TableContext);
    const { leftOriginalColumns, originalColumns, rightOriginalColumns, customized } = tableStore;
    const [customizedColumns, setCustomizedColumns] = useState(() => [...leftOriginalColumns, ...originalColumns, ...rightOriginalColumns].filter(column => column.key !== SELECTION_KEY));
    const tableRecord = useMemo(() => new DataSet({
        data: [
            {
                heightType: tableStore.heightType,
                height: tableStore.totalHeight,
                heightDiff: diff(tableStore.totalHeight),
                aggregation: tableStore.aggregation,
                size: tableStore.size,
                parityRow: tableStore.parityRow,
                aggregationExpandType: tableStore.aggregationExpandType,
            },
        ],
        events: {
            update({ record, name, value }) {
                record.setState(HEIGHT_CHANGE_KEY, (record.getState(HEIGHT_CHANGE_KEY) || 0) + 1);
                const { tempCustomized, props: { columns, children } } = tableStore;
                if (tempCustomized) {
                    set(tempCustomized, name, value);
                    if (name === 'heightType') {
                        if (value === TableHeightType.fixed) {
                            set(tempCustomized, 'height', record.get('height'));
                        }
                        else if (value === TableHeightType.flex) {
                            set(tempCustomized, 'heightDiff', record.get('heightDiff'));
                        }
                    }
                    else if (name === 'height' && record.get('heightType') === TableHeightType.fixed) {
                        record.set('heightDiff', diff(value));
                        set(tempCustomized, 'heightType', TableHeightType.fixed);
                    }
                    else if (name === 'heightDiff' && record.get('heightType') === TableHeightType.flex) {
                        record.set('height', diff(value));
                        set(tempCustomized, 'heightType', TableHeightType.flex);
                    }
                    else if (name === 'aggregation') {
                        setCustomizedColumns(columns
                            ? flatten(mergeDefaultProps(columns, value, tempCustomized.columns).slice(0, 3))
                            : flatten(normalizeColumns(children, value, tempCustomized.columns).slice(0, 3)));
                    }
                }
                record.setState(HEIGHT_CHANGE_KEY, record.getState(HEIGHT_CHANGE_KEY) - 1);
                if (record.getState(HEIGHT_CHANGE_KEY) === 0) {
                    tableStore.node.handleHeightTypeChange();
                    record.setState(HEIGHT_CHANGE_KEY, undefined);
                }
            },
        },
    }).current, [tableStore]);
    const columnDataSet = useMemo(() => new DataSet({
        data: normalizeColumnsToTreeData(customizedColumns),
        paging: false,
        primaryKey: 'key',
        idField: 'key',
        parentField: 'parentKey',
        fields: [
            { name: 'lock', group: true },
        ],
        events: {
            update({ record, name, value }) {
                if (name === 'hidden') {
                    const { children: records } = record;
                    if (records) {
                        records.forEach(child => child.set(name, value));
                    }
                }
                const { tempCustomized } = tableStore;
                if (tempCustomized) {
                    const { key } = record;
                    if (!tempCustomized.columns[key]) {
                        tempCustomized.columns[key] = {};
                    }
                    tempCustomized.columns[key][name] = value;
                }
            },
        },
    }), [customizedColumns, tableStore]);
    const handleRestoreTable = useCallback(action((e) => {
        e.stopPropagation();
        const { originalHeightType } = tableStore;
        tableStore.node.handleHeightTypeChange(true);
        tableRecord.init({
            heightType: originalHeightType,
            height: tableStore.totalHeight,
            heightDiff: diff(tableStore.totalHeight),
        });
    }), [tableRecord, tableStore]);
    const handleRestoreColumns = useCallback(action((e) => {
        e.stopPropagation();
        const { props: { columns, children } } = tableStore;
        const aggregation = tableRecord.get('aggregation');
        setCustomizedColumns(columns
            ? flatten(mergeDefaultProps(columns, aggregation).slice(0, 3))
            : flatten(normalizeColumns(children, aggregation).slice(0, 3)));
        tableStore.tempCustomized.columns = {};
    }), [tableRecord, tableStore]);
    const handleOption = useCallback(() => ({
        className: `${prefixCls}-customization-option`,
    }), [prefixCls]);
    const handleCollapseChange = useCallback(action((key) => {
        tableStore.customizedActiveKey = [].concat(key);
    }), [tableStore]);
    useEffect(action(() => {
        tableStore.tempCustomized = {
            height: tableStore.totalHeight,
            heightDiff: diff(tableStore.totalHeight),
            ...toJS(customized),
        };
    }), [tableStore]);
    useEffect(() => {
        if (handleOk) {
            handleOk(action(() => {
                const { tempCustomized, aggregation, props: { onAggregationChange } } = tableStore;
                tableStore.tempCustomized = { columns: {} };
                tableStore.saveCustomized(tempCustomized);
                tableStore.initColumns();
                tableStore.node.handleHeightTypeChange();
                const { aggregation: customAggregation } = tempCustomized;
                if (onAggregationChange && customAggregation !== undefined && customAggregation !== aggregation) {
                    onAggregationChange(customAggregation);
                }
            }));
        }
        if (handleCancel) {
            handleCancel(action(() => {
                tableStore.tempCustomized = { columns: {} };
                tableStore.node.handleHeightTypeChange();
            }));
        }
    }, [handleOk, handleCancel, columnDataSet, tableStore]);
    const renderIcon = useCallback(({ isActive }) => React.createElement(Icon, { type: isActive ? 'expand_more' : 'navigate_next' }), []);
    const tableHeightType = tableRecord.get('heightType');
    return (React.createElement(Collapse, { activeKey: tableStore.customizedActiveKey.slice(), onChange: handleCollapseChange, expandIcon: renderIcon, expandIconPosition: "text-right", className: `${prefixCls}-customization`, ghost: true },
        React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, $l('Table', 'display_settings')), key: "display" },
            React.createElement(Form, { className: `${prefixCls}-customization-form`, record: tableRecord, labelLayout: LabelLayout.float },
                tableStore.hasAggregationColumn && (React.createElement(SelectBox, { name: "aggregation", label: $l('Table', 'view_display'), mode: ViewMode.button },
                    React.createElement(Option, { value: false, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'tiled_view'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-not-aggregation` }))),
                    React.createElement(Option, { value: true, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'aggregation_view'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-aggregation` }))))),
                React.createElement(SelectBox, { name: "size", label: $l('Table', 'density_display'), mode: ViewMode.button },
                    React.createElement(Option, { value: Size.default, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'normal'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-size-default` }))),
                    React.createElement(Option, { value: Size.small, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'compact'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-size-small` })))),
                React.createElement(SelectBox, { name: "parityRow", label: $l('Table', 'parity_row'), mode: ViewMode.button },
                    React.createElement(Option, { value: false, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'normal'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-no-parity-row` }))),
                    React.createElement(Option, { value: true, className: `${prefixCls}-customization-select-view-option` },
                        React.createElement(Tooltip, { title: $l('Table', 'parity_row'), placement: "top" },
                            React.createElement("div", { className: `${prefixCls}-customization-select-view-option-inner ${prefixCls}-customization-parity-row` })))))),
        React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, $l('Table', 'table_settings')), key: "table", extra: React.createElement(Button, { className: `${prefixCls}-customization-header-button`, color: ButtonColor.primary, funcType: FuncType.flat, size: Size.small, onClick: handleRestoreTable }, $l('Table', 'restore_default')) },
            React.createElement(Form, { className: `${prefixCls}-customization-form`, record: tableRecord, labelLayout: LabelLayout.float },
                React.createElement(SelectBox, { vertical: true, name: "heightType", label: $l('Table', 'height_settings'), onOption: handleOption },
                    React.createElement(Option, { value: TableHeightType.auto }, $l('Table', 'auto_height')),
                    React.createElement(Option, { value: TableHeightType.fixed },
                        React.createElement("span", { className: `${prefixCls}-customization-option-label` }, $l('Table', 'fixed_height')),
                        React.createElement(ObserverNumberField, { className: `${prefixCls}-customization-option-input`, labelLayout: LabelLayout.none, name: "height", disabled: tableHeightType !== TableHeightType.fixed, step: 1 })),
                    React.createElement(Option, { value: TableHeightType.flex },
                        React.createElement("span", { className: `${prefixCls}-customization-option-label` }, $l('Table', 'flex_height')),
                        React.createElement(ObserverNumberField, { groupClassName: `${prefixCls}-customization-option-input`, labelLayout: LabelLayout.none, name: "heightDiff", disabled: tableHeightType !== TableHeightType.flex, showHelp: ShowHelp.tooltip, help: $l('Table', 'flex_height_help'), step: 1 }))),
                tableRecord.get('aggregation') && (React.createElement(SelectBox, { name: "aggregationExpandType", label: $l('Table', 'row_expand_settings') },
                    React.createElement(Option, { value: "cell" }, $l('Table', 'expand_cell')),
                    React.createElement(Option, { value: "row" }, $l('Table', 'expand_row')),
                    React.createElement(Option, { value: "column" }, $l('Table', 'expand_column')))))),
        React.createElement(CollapsePanel, { header: React.createElement("span", { className: `${prefixCls}-customization-panel-title` }, $l('Table', 'column_settings')), key: "columns", extra: React.createElement(Button, { className: `${prefixCls}-customization-header-button`, color: ButtonColor.primary, funcType: FuncType.flat, size: Size.small, onClick: handleRestoreColumns }, $l('Table', 'restore_default')) },
            React.createElement(ColumnGroups, { dataSet: columnDataSet }))));
});
CustomizationSettings.displayName = 'CustomizationSettings';
export default CustomizationSettings;
